import { useState } from "react";
import { LegalShell } from "../components/LegalShell";
import { useSeo } from "../lib/useSeo";
import { Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

const ContactUs = () => {
    useSeo({
        title: "Contact Us | Shillong Morning Teer Result Today",
        description: "Contact the Shillong Morning Teer Result Today team for support, feedback or partnership. Reach us via email, WhatsApp or Telegram.",
        keywords: "contact shillong morning teer, morning teer support, morningteerkhela contact",
        path: "/contact-us",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Contact Us", path: "/contact-us" },
        ],
    });

    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        setSending(true);
        // Since there is no email backend by default, we open the user's mail client.
        const body = `Name: ${form.name}%0D%0AEmail: ${form.email}%0D%0A%0D%0A${encodeURIComponent(form.message)}`;
        window.location.href = `mailto:support@shillongmorningteerresulttoday.com?subject=${encodeURIComponent(form.subject || "Website enquiry")}&body=${body}`;
        setTimeout(() => {
            setSending(false);
            toast.success("Opening your email client — we'll get back within 24 hours.");
        }, 500);
    };

    return (
        <LegalShell title="Contact Us" subtitle="Reach out — we typically reply within 24 hours." breadcrumb="Contact Us">
            <p>
                We love hearing from readers. Whether you have a suggestion, a correction, or a
                partnership enquiry, drop us a message. Our team is small, friendly and quick.
            </p>

            <div className="grid md:grid-cols-2 gap-4 not-prose my-6">
                <a href="mailto:support@shillongmorningteerresulttoday.com" data-testid="contact-email-card"
                    className="card-crisp p-5 flex items-center gap-3 hover:shadow-lg transition-shadow">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-900 grid place-items-center"><Mail className="h-5 w-5" /></div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</div>
                        <div className="font-display font-bold text-blue-950">support@shillongmorningteerresulttoday.com</div>
                    </div>
                </a>
                <a href="https://wa.me/919999999999" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-card"
                    className="card-crisp p-5 flex items-center gap-3 hover:shadow-lg transition-shadow">
                    <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center"><MessageCircle className="h-5 w-5" /></div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">WhatsApp</div>
                        <div className="font-display font-bold text-blue-950">+91 99999 99999</div>
                    </div>
                </a>
            </div>

            <h2>Send us a message</h2>
            <form onSubmit={submit} className="not-prose card-crisp p-5 md:p-6 mt-4 space-y-4" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-blue-950 uppercase tracking-widest">Name</label>
                        <input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                            data-testid="contact-name-input"
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-900" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-blue-950 uppercase tracking-widest">Email</label>
                        <input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                            data-testid="contact-email-input"
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-900" />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-semibold text-blue-950 uppercase tracking-widest">Subject</label>
                    <input value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})}
                        data-testid="contact-subject-input"
                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-900" />
                </div>
                <div>
                    <label className="text-xs font-semibold text-blue-950 uppercase tracking-widest">Message</label>
                    <textarea required rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})}
                        data-testid="contact-message-input"
                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-900" />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                    className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold">
                    <Send className="h-4 w-4" /> {sending ? "Opening…" : "Send Message"}
                </button>
            </form>
        </LegalShell>
    );
};

export default ContactUs;
